import React from 'react';
import useEsMobile from '../../hooks/useEsMobile';

/**
 * Adapta las columnas de un `<Table>` a mobile APILANDO datos en una celda,
 * en vez de ocultarlos o de obligar a scrollear a la derecha.
 *
 * POR QUE ESTE CAMINO Y NO OTRO. Habia tres:
 *
 *  1. `responsive: ['md']` en cada columna (nativo de antd): una linea, pero
 *     ESCONDE informacion. En un panel de gestion, el dato que no se ve es un
 *     dato que no existe. Descartado.
 *  2. Renderizar `<List>` o cards en mobile: se ve mejor, pero son DOS caminos
 *     de render por tabla. Con ~52 tablas en el panel son 52 implementaciones
 *     duplicadas, y se pierde gratis lo que `<Table>` ya resuelve: paginacion,
 *     loading, empty state, `rowSelection`.
 *  3. Este: mismo `<Table>`, se cambia el array `columns`.
 *
 * El 3 gana por una razon concreta: `columns` es DATO, no componente. No se
 * bifurca el arbol de render, se bifurca un objeto de configuracion. Todo lo
 * que `<Table>` trae sigue funcionando sin tocarlo.
 *
 * LA TRAMPA DEL PATRON — y es la razon por la que este helper existe en vez de
 * escribir el JSX a mano en cada tabla: las columnas NO son datos planos.
 * `estado` en el Dashboard es un `<Tag>` de color; `categoria` es un render con
 * fallback a dos shapes distintos del endpoint. Una celda apilada que lea
 * `record[dataIndex]` crudo devolveria el estado sin color y la categoria rota.
 * Por eso la config de mobile referencia columnas POR SU `key` y este helper
 * las busca en el array de desktop: lo que se apila es la columna real, con su
 * `render` incluido. Una sola fuente de verdad por celda.
 *
 * ETIQUETAS: van donde el dato es ambiguo, no van donde el dato se explica
 * solo. `Corral: 12 · RP: 4501` las necesita (dos numeros pelados, sin etiqueta
 * no se sabe cual es cual). Un `<Tag>` verde que dice "Activo" no la necesita.
 * Por eso `etiqueta` es opcional por campo y no un flag global.
 *
 * @example
 *   const { columnas, propsTabla } = useColumnasResponsive({
 *     desktop: eventosColumns,
 *     scroll: { x: 'max-content' },
 *     mobile: {
 *       title: 'Evento',
 *       principal: 'nombre_evento',
 *       secundarios: [{ key: 'fecha_inicio', etiqueta: 'F.I' }, { key: 'estado' }],
 *       conservar: [{ key: 'action', width: 110 }],   // o 'action' pelado
 *     },
 *   });
 *   <Table columns={columnas} {...propsTabla} dataSource={...} />
 */

/** Lee el valor crudo que antd le pasaria al `render` de la columna. Soporta
 *  `dataIndex` como string y como array (path anidado), igual que antd. */
const leerValor = (columna, record) => {
  const { dataIndex } = columna;
  if (dataIndex === undefined) return undefined;
  return Array.isArray(dataIndex)
    ? dataIndex.reduce((obj, clave) => obj?.[clave], record)
    : record?.[dataIndex];
};

/* ¿Este campo no tiene dato?
 *
 * Se mira el valor CRUDO y no lo que devuelve el `render`, porque un render
 * puede devolver JSX y no hay forma honesta de preguntarle a un nodo de React
 * si "esta vacio".
 *
 * SIN `dataIndex` LA RESPUESTA ES QUE NO, y esto no es un detalle: `leerValor`
 * devuelve `undefined` en dos situaciones que no son la misma —"el campo esta
 * vacio" y "no hay campo, esta columna se calcula"—. Aplastarlas en una
 * escondia TODAS las columnas definidas solo por `render`: el <Tag> de estado
 * del Dashboard, y la categoria en las dos tablas. Justo las tres que el
 * docblock de arriba usa como ejemplo de por que este helper existe. Y fallaba
 * callado: `buscarColumna` encontraba la columna, no habia warning, y solo se
 * notaba en un telefono.
 *
 * Una columna calculada no tiene nada que se pueda juzgar vacio. Se dibuja. */
const vacio = (columna, record) => {
  if (columna.dataIndex === undefined) return false;
  const v = leerValor(columna, record);
  return v == null || v === '';
};

/** Renderiza una celda con la MISMA firma que usa antd: `render(valor, record,
 *  index)`. Si la columna no trae `render`, cae al valor crudo. */
const renderizarCelda = (columna, record, index) => {
  const valor = leerValor(columna, record);
  return columna.render ? columna.render(valor, record, index) : valor;
};

/* Busca una columna por `key` (o por `dataIndex`, que es lo que antd usa de
 * fallback cuando no hay `key`).
 *
 * SI NO LA ENCUENTRA: avisa por consola y devuelve `undefined` para que el
 * campo se saltee, en vez de tirar una excepcion. Tirar romperia la pantalla
 * ENTERA y solo en mobile — un `key` renombrado dejaria a los usuarios de
 * telefono sin panel. Saltear degrada a un dato de menos y grita en la consola
 * de desarrollo. Es el mismo criterio de mejora progresiva que ya usa el badge
 * de notificaciones en los layouts. */
const buscarColumna = (columnas, clave) => {
  /* El `dataIndex` array se compara unido por puntos ('animal.raza'). `leerValor`
   * ya soporta esa forma; sin esto, el modulo la entiende para LEER pero no
   * para BUSCAR, que es una inconsistencia adentro del mismo archivo. */
  const columna = columnas.find(c => (
    c.key === clave ||
    (Array.isArray(c.dataIndex) ? c.dataIndex.join('.') === clave : c.dataIndex === clave)
  ));
  /* El guard de NODE_ENV no es cosmetico: `buscarColumna` corre en el CUERPO
   * DEL RENDER, asi que un `key` renombrado no loguearia una vez — loguearia
   * en cada render de cada tabla que lo referencie, en el navegador del
   * cliente, para siempre. Degradar el campo es lo que queriamos; inundarle la
   * consola no era parte del trato. */
  if (!columna && process.env.NODE_ENV !== 'production') {
    console.error(
      `useColumnasResponsive: no hay ninguna columna con key "${clave}" en la ` +
      `config de desktop. El campo se omite en mobile. Columnas disponibles: ` +
      columnas.map(c => c.key ?? c.dataIndex).join(', ')
    );
  }
  return columna;
};

export default function useColumnasResponsive({ desktop, mobile, scroll }) {
  const esMobile = useEsMobile();

  /* EL `scroll.x` TIENE QUE MORIR EN MOBILE, y esta es la razon por la que el
   * helper devuelve tambien los props de la tabla en vez de solo las columnas.
   *
   * CR-2 le puso `scroll={{ x: 'max-content' }}` a 48 tablas, y esta bien para
   * desktop. Pero `width: max-content` significa, literalmente, "el ancho que
   * tomaria el contenido SIN cortar lineas". Con eso puesto, el `flex-wrap` de
   * la celda apilada NO SE DISPARA NUNCA: en vez de envolver a dos lineas, la
   * tabla se ensancha y volves a scrollear a la derecha. O sea, todo este
   * trabajo seria invisible.
   *
   * En mobile la tabla tiene que respetar el ancho de su contenedor para que el
   * contenido envuelva. Se devuelve como `propsTabla` y no como `scroll` pelado
   * para que el dia que haga falta otro prop solo-de-mobile no haya que tocar
   * las 52 llamadas. */
  /* OJO CON LOS FALLBACKS: `scroll: undefined` SOLO vale cuando de verdad
   * devolvemos la celda apilada. Los dos `return` de abajo entregan las
   * columnas de DESKTOP —las anchas, las que necesitan el scroll para leerse—
   * asi que sacarles el scroll ahi seria lo peor de los dos mundos: 8 columnas
   * apretadas en 350px y sin forma de correrlas. Peor todavia: quedaria peor
   * que antes de que este helper existiera.
   *
   * Pasa en dos casos reales: una tabla que llama sin `mobile` (opt-out
   * legitimo, el campo es opcional) y una tabla a la que le renombraron el
   * `key` del `principal`. */
  /* `size: 'small'` en mobile baja el padding de celda de 16px a 8px de cada
   * lado. Son 16px que le devuelve a CADA columna, y en 375px eso no es
   * cosmetico: la columna de acciones deja de competirle el ancho a la celda
   * apilada, que es la que lleva la informacion.
   *
   * Va en `propsTabla` y no forzado adentro del componente para que el que
   * llama pueda pisarlo: como se spreadea primero, un `size` puesto despues
   * gana. Las tablas del Dashboard ya venian con `size="small"` propio y
   * siguen igual. */
  // `scroll: undefined` sin ternario: a este objeto solo se llega despues del
  // guard de `!esMobile`, asi que aca esMobile SIEMPRE es true. Un ternario
  // ahi seria una rama que no existe.
  const propsApilada = { scroll: undefined, size: 'small' };
  const propsDesktop = { scroll };

  if (!esMobile || !mobile) return { columnas: desktop, propsTabla: propsDesktop };

  const columnaPrincipal = buscarColumna(desktop, mobile.principal);
  if (!columnaPrincipal) return { columnas: desktop, propsTabla: propsDesktop };

  const secundarios = (mobile.secundarios ?? [])
    // Se acepta el string pelado igual que en `conservar`, mas abajo. Antes solo
    // `conservar` normalizaba, y esa asimetria es una trampa que falla EN
    // SILENCIO: desestructurar { key } de un string da undefined, buscarColumna
    // no encuentra nada, el .filter descarta el campo y en mobile desaparece
    // sin decir nada — el console.error de buscarColumna esta guardado por
    // NODE_ENV !== 'production', asi que en produccion no se entera nadie.
    .map(item => (typeof item === 'string' ? { key: item } : item))
    .map(({ key, etiqueta }) => ({ columna: buscarColumna(desktop, key), etiqueta }))
    .filter(s => s.columna);

  const columnaApilada = {
    title: mobile.title ?? columnaPrincipal.title,
    key: 'apilada-mobile',
    render: (_, record, index) => (
      <div>
        <div className="celda-apilada__principal">
          {renderizarCelda(columnaPrincipal, record, index)}
        </div>
        {secundarios.length > 0 && (
          /* `flex-wrap` en vez de un alto fijo A PROPOSITO: el contenido varia.
           * "Cat: Toro" entra en una linea y "Cat: Vaquillona preñada" no. Con
           * dos lineas hardcodeadas, la corta deja un hueco y la larga se
           * desborda igual. Envolviendo, usa una linea cuando alcanza y dos
           * cuando hace falta, sin que nadie decida nada. */
          <div className="celda-apilada__secundarios">
            {secundarios.map(({ columna, etiqueta }) => {
              /* Un secundario SIN VALOR no se dibuja. Sin esto, un animal sin
               * precio mostraba un — flotando solo al final de la celda: el
               * `render` traduce null a guion, y como al precio no le pusimos
               * etiqueta —"el signo $ se explica solo"— cuando no hay signo no
               * queda nada que explique nada. Ausencia de dato, ausencia de
               * linea. Ver `vacio` para el caso de las columnas calculadas. */
              if (vacio(columna, record)) return null;
              return (
                <span
                  className="celda-apilada__dato"
                  key={columna.key ?? columna.dataIndex}
                >
                  {etiqueta && (
                    <span className="celda-apilada__etiqueta">{etiqueta}:</span>
                  )}
                  {renderizarCelda(columna, record, index)}
                </span>
              );
            })}
          </div>
        )}
      </div>
    ),
  };

  /* Las columnas conservadas se toman TAL CUAL del array de desktop —misma
   * referencia, mismo `render`— salvo `width`, que se descarta: un ancho fijo
   * pensado para una grilla de 5 columnas (el `width: 80` de Corral, por
   * ejemplo) pelea con un layout de 2. En mobile el ancho lo reparte la tabla. */
  /* `conservar` toma una key suelta o un `{ key, width }`.
   *
   * EL `width` DE DESKTOP SE DESCARTA SIEMPRE: esta pensado para una grilla de
   * 5 o 6 columnas y no significa nada en una de 2 (el `width: 80` de Corral es
   * el ejemplo). Pero descartarlo y no poner NADA tampoco alcanza: con
   * `table-layout: auto` el navegador reparte por contenido, y una columna de
   * acciones con tres botones y el titulo "Acciones" pide casi tanto ancho como
   * la celda apilada — que ademas envuelve, asi que su contenido "mide" poco.
   * Resultado: mitad y mitad, con la columna que importa apretada.
   *
   * Por eso el ancho de mobile se declara aparte: es una decision de esta
   * vista, no una herencia de la otra. */
  const conservadas = (mobile.conservar ?? [])
    .map(item => (typeof item === 'string' ? { key: item } : item))
    .map(({ key, width }) => {
      const columna = buscarColumna(desktop, key);
      if (!columna) return null;
      const { width: _descartado, ...resto } = columna;
      return width == null ? resto : { ...resto, width };
    })
    .filter(Boolean);

  return { columnas: [columnaApilada, ...conservadas], propsTabla: propsApilada };
}
